import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Row, Col, Input } from 'reactstrap';
import ReactTable from 'react-table';
import 'bootstrap/dist/css/bootstrap.css';
import 'react-table/react-table.css';
import '../DashboardTiles/Library/PortalDashboardTileStyles.css';
import '../DashboardTiles/Dashboards/Dashboards.css';
import * as utilities from '../../utilities';
import CompanyContext from '../../auth/CompanyContext';
import Loading from '../Loading/Loading';

const normalizeValue = (value) => String(value || '').trim().toLowerCase();

const filterCaseInsensitive = (filter, row) => {
    const id = filter.pivotId || filter.id;
    const content = row[id];
    if (typeof content !== 'undefined' && content !== null) {
        return normalizeValue(content).includes(normalizeValue(filter.value));
    }
    return true;
};

const ContactsAccounts2 = ({
    contactGUID,
    clientToken,
    onAccountsChanged,
    isActive = false,
    selectedContactHouseholds = [],
    contactAccountsLoading = false,
    householdRows = [],
    householdsLoading = false,
    onRefreshContactAccounts,
    onRefreshHouseholds
}) => {
    const companyCtx = useContext(CompanyContext);
    const contactAccountsRef = useRef([]);

    const [isSaving, setIsSaving] = useState(false);
    const [households, setHouseholds] = useState([]);
    const [householdChecks, setHouseholdChecks] = useState([]);
    const [pendingHouseholds, setPendingHouseholds] = useState([]);
    const [pageSize, setPageSizeState] = useState(
        localStorage.getItem('contactsAccountsPageSize') != null
            ? parseInt(localStorage.getItem('contactsAccountsPageSize'))
            : 20
    );

    useEffect(() => {
        const rows = householdRows || [];
        const firstDate = (rows.find(row => row?.date != null) || {}).date || '';

        const groupedMap = rows.reduce((acc, item) => {
            const householdKey = item?.household || item?.householdName || '';
            if (!householdKey) return acc;
            if (!acc[householdKey]) acc[householdKey] = [];
            acc[householdKey].push({
                ...item,
                displayName: String(item?.displayName || '').replace(' - ' + item?.account, '')
            });
            return acc;
        }, {});

        const groupedData = Object.keys(groupedMap).map((householdKey) => {
            const details = groupedMap[householdKey];
            const householdName = details[0]?.householdName || householdKey;
            return {
                household: householdKey,
                householdName,
                houseMarketValue: null,
                details,
                date: firstDate,
                includeMV: false
            };
        });

        contactAccountsRef.current = (selectedContactHouseholds || []).map(item => String(item || '').trim()).filter(Boolean);
        setHouseholds(groupedData);
        const accountSet = new Set(contactAccountsRef.current.map(normalizeValue));
        setHouseholdChecks(groupedData.map(item => ({
            household: item.household,
            checked: accountSet.has(normalizeValue(item.household))
        })));
    }, [householdRows, selectedContactHouseholds]);

    const getCheckedAccounts = useCallback((checks = []) => {
        return checks
            .filter(item => item.checked)
            .map(item => item.household);
    }, []);

    const saveContactAccounts = useCallback((accounts = []) => {
        if (!contactGUID) return Promise.resolve(false);

        return fetch(`/api/contactAccounts/${contactGUID}`, {
            method: 'PUT',
            body: JSON.stringify({
                accounts
            }),
            headers: {
                'Content-Type': 'application/json',
                'Authorization': 'Bearer ' + (clientToken || ""),
                'X-Database': companyCtx.company
            }
        })
            .then(results => results.json())
            .then(results => {
                if (results.success) {
                    contactAccountsRef.current = accounts;
                    if (typeof onAccountsChanged === 'function') {
                        onAccountsChanged(accounts);
                    }
                    return true;
                }

                utilities.addToast('Unable to save contact accounts.', 5, 'danger');
                return false;
            })
            .catch(() => {
                utilities.addToast('An error has occurred.', 5, 'danger');
                return false;
            });
    }, [contactGUID, clientToken, companyCtx.company, onAccountsChanged]);

    const isRefreshing = householdsLoading || contactAccountsLoading;
    const isWorking = isSaving || pendingHouseholds.length > 0;
    const hasCheckboxes = householdChecks.length > 0;
    const showControlRow = hasCheckboxes || typeof onRefreshContactAccounts === 'function' || typeof onRefreshHouseholds === 'function';
    const allChecked = householdChecks.length > 0 && householdChecks.every(item => item.checked);
    const someChecked = householdChecks.some(item => item.checked) && !allChecked;
    const statusMessage = isWorking
        ? 'Saving account selections...'
        : (isRefreshing ? 'Loading account selections...' : '');

    const tableData = useMemo(() => {
        const checkMap = new Map(householdChecks.map(item => [item.household, item.checked]));
        return households.map(item => ({
            ...item,
            checked: !!checkMap.get(item.household)
        }));
    }, [households, householdChecks]);

    const onToggleHousehold = useCallback(async (household, checked) => {
        setPendingHouseholds(prev => (prev.includes(household) ? prev : [...prev, household]));

        const nextChecks = householdChecks.map(item => (
            item.household !== household ? item : { ...item, checked }
        ));
        setHouseholdChecks(nextChecks);

        try {
            const success = await saveContactAccounts(getCheckedAccounts(nextChecks));
            if (!success) {
                if (typeof onRefreshContactAccounts === 'function') {
                    onRefreshContactAccounts();
                }
            }
        } finally {
            setPendingHouseholds(prev => prev.filter(item => item !== household));
        }
    }, [householdChecks, saveContactAccounts, getCheckedAccounts, onRefreshContactAccounts]);

    const onSelectAll = useCallback(async () => {
        setIsSaving(true);
        const nextChecks = householdChecks.map(item => ({ ...item, checked: true }));
        setHouseholdChecks(nextChecks);
        try {
            const success = await saveContactAccounts(getCheckedAccounts(nextChecks));
            if (!success && typeof onRefreshContactAccounts === 'function') onRefreshContactAccounts();
        } finally {
            setIsSaving(false);
        }
    }, [householdChecks, saveContactAccounts, getCheckedAccounts, onRefreshContactAccounts]);

    const onSelectNone = useCallback(async () => {
        setIsSaving(true);
        const nextChecks = householdChecks.map(item => ({ ...item, checked: false }));
        setHouseholdChecks(nextChecks);
        try {
            const success = await saveContactAccounts([]);
            if (!success && typeof onRefreshContactAccounts === 'function') onRefreshContactAccounts();
        } finally {
            setIsSaving(false);
        }
    }, [householdChecks, saveContactAccounts, onRefreshContactAccounts]);

    const setPageSize = useCallback((nextPageSize) => {
        const parsed = parseInt(nextPageSize);
        const safePageSize = Number.isNaN(parsed) ? 20 : parsed;
        setPageSizeState(safePageSize);
        localStorage.setItem('contactsAccountsPageSize', safePageSize);
    }, []);

    const columns = useMemo(() => [
        {
            Header: () => (
                <div className="text-center">
                    <Input
                        type="checkbox"
                        checked={allChecked}
                        innerRef={(el) => {
                            if (el) el.indeterminate = someChecked;
                        }}
                        disabled={isWorking || !hasCheckboxes}
                        title={someChecked ? 'Some selected' : (allChecked ? 'Select none' : 'Select all')}
                        onChange={(e) => {
                            if (e.target.checked) {
                                onSelectAll();
                            } else {
                                onSelectNone();
                            }
                        }}
                        style={{ position: 'relative', margin: '0 auto' }}
                    />
                </div>
            ),
            id: 'checked',
            width: 60,
            sortable: false,
            filterable: false,
            Cell: ({ original }) => (
                <Input
                    type="checkbox"
                    checked={!!original.checked}
                    disabled={isSaving || pendingHouseholds.includes(original.household)}
                    onChange={(e) => onToggleHousehold(original.household, e.target.checked)}
                    style={{ position: 'relative', marginTop: 0 }}
                />
            )
        },
        {
            Header: 'Household',
            accessor: 'household',
            headerStyle: { textAlign: 'left' },
            style: { textAlign: 'left' },
            filterable: true
        }
    ], [
        isSaving,
        pendingHouseholds,
        onToggleHousehold,
        isWorking,
        hasCheckboxes,
        allChecked,
        someChecked,
        onSelectAll,
        onSelectNone
    ]);

    if (!isActive) return null;

    return (
        <>
            {!hasCheckboxes && statusMessage && (
                <Row className='mb-2 text-left'>
                    <Col className='ml-5 text-muted'>
                        {/* <Loading /> */}
                        {statusMessage}
                    </Col>
                </Row>
            )}
            <Row>
                <Col className='ml-5 mr-3'>
                    <ReactTable
                        data={tableData}
                        columns={columns}
                        loading={isRefreshing}
                        defaultFilterMethod={filterCaseInsensitive}
                        pageSize={pageSize}
                        onPageSizeChange={setPageSize}
                        pageSizeOptions={[10, 20, 50, 100, 250]}
                        showPageSizeOptions={true}
                        minRows={0}
                        noDataText={isRefreshing ? 'Loading account selections...' : 'No households available'}
                    />
                </Col>
            </Row>
            {showControlRow && (
                <Row className='mt-3 text-left align-items-center'>
                    {/* <Col md="auto" className='ml-5'>
                        {typeof onRefreshContactAccounts === 'function' && (
                            <Button color="secondary" disabled={isWorking} onClick={onRefreshContactAccounts}>
                                Refresh Accounts
                            </Button>
                        )}
                        {typeof onRefreshHouseholds === 'function' && (
                            <Button color="secondary" className="ml-3" disabled={isWorking} onClick={onRefreshHouseholds}>
                                Refresh Households
                            </Button>
                        )}
                    </Col> */}
                    {statusMessage && (
                        <Col md="auto" className="text-muted mt-2 mt-md-0">
                            <Loading />
                            {statusMessage}
                        </Col>
                    )}
                </Row>
            )}
        </>
    );
};

export default ContactsAccounts2;
